import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import ContentImageSection from "../../components/sections/ContentImageSection";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiCode, FiShield, FiZap, FiCheckCircle } from "react-icons/fi";
import bgImage from "../../images/img3.jpg";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const VibeCodingPage = () => {
  usePageMetadata({
    title: 'Vibe Coding Cleanup - Bercastle',
    description: 'Code architecture review and cleanup for AI-generated code. We restructure your codebase following industry best practices.',
    pageType: 'service',
    content: {
      service: {
        name: "Vibe Coding Cleanup",
        tagline: "Professional Code Review & Restructuring",
        description: "Transform AI-generated code into production-ready, maintainable software following industry best practices.",
        url: "/services/vibe-coding"
      },
      capabilities: [
        {
          name: "Code Architecture Review",
          description: "We analyze your AI-generated codebase and restructure it following industry best practices.",
          review_includes: ["Architecture assessment", "Code quality analysis", "Performance evaluation", "Security audit", "Scalability review"]
        },
        {
          name: "Testing & Quality Assurance",
          description: "Comprehensive testing strategy to ensure reliability.",
          services: ["Unit test coverage", "Integration testing", "E2E test automation", "Performance testing", "Security testing"]
        },
        {
          name: "Performance Optimization",
          description: "Optimize your codebase for speed, efficiency, and scalability.",
          optimizations: ["Database query optimization", "Caching strategies", "Code refactoring", "Bundle size reduction", "API optimization"]
        },
        {
          name: "Security Hardening",
          description: "Identify and fix security vulnerabilities.",
          measures: ["Vulnerability scanning", "Authentication & authorization", "Data encryption", "Secure API design", "Compliance review"]
        }
      ],
      common_issues: ["Inconsistent code style", "Poor error handling", "Missing tests", "Security vulnerabilities", "Performance bottlenecks", "Lack of documentation", "Tight coupling", "Code duplication"],
      process: ["Assessment", "Planning", "Implementation", "Testing", "Documentation", "Training"],
      value_propositions: [
        "Expert developers with production experience",
        "Industry best practices",
        "Comprehensive testing",
        "Clear documentation",
        "Knowledge transfer included",
        "Ongoing support available"
      ],
      meta: {
        source_url: "https://bercastle.com/services/vibe-coding",
        service_category: "Code Cleanup"
      }
    }
  });
  const cleanupServices = [
    {
      icon: FiCode,
      title: "Code Architecture Review",
      description: "We analyze your AI-generated codebase and restructure it following industry best practices and design patterns."
    },
    {
      icon: FiCheckCircle,
      title: "Testing & Quality Assurance",
      description: "Add comprehensive testing suites to ensure your application works as expected and stays that way."
    },
    {
      icon: FiShield,
      title: "Security Hardening",
      description: "Identify and fix security vulnerabilities in AI-generated code before they become problems."
    },
    {
      icon: FiZap,
      title: "Performance Optimization",
      description: "Optimize your application for speed, efficiency, and scalability in production environments."
    }
  ];

  const processSteps = [
    {
      icon: FiCode,
      title: "Code Assessment",
      description: "Comprehensive analysis of your AI-generated codebase to identify issues, dependencies, and improvement opportunities."
    },
    {
      icon: FiCode,
      title: "Architecture Planning",
      description: "Design a scalable architecture that maintains your prototype's functionality while adding structure and best practices."
    },
    {
      icon: FiCode,
      title: "Refactoring & Testing",
      description: "Systematically refactor code modules while implementing comprehensive testing to ensure nothing breaks."
    },
    {
      icon: FiShield,
      title: "Security & Performance",
      description: "Harden security, optimize performance, and prepare your application for production deployment."
    },
    {
      icon: FiCheckCircle,
      title: "Documentation & Handoff",
      description: "Complete documentation, training, and smooth transition to your team for ongoing development."
    }
  ];
  return (
    <Layout classmain="vibe-coding-page">
      <Seo
        title='Vibe Coding Cleanup - OnScript AI'
        metaDesciption='Transform AI prototypes into production-ready applications. Expert vibe coding cleanup specialists turn your LLM-generated code into robust, maintainable software.'
      />
      <Header />

      <ServiceHero
        badge="Vibe Coding Cleanup"
        title="From AI Prototype to"
        titleHighlight="Production-Ready Code"
        description="Vibe coding accelerates development, but production demands quality. We transform your AI-generated prototypes into robust, maintainable applications."
        ctaText="Clean Up Your Code"
        ctaLink="/contact"
      />

      {/* What is Vibe Coding Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <ContentImageSection
            image={bgImage}
            imageAlt="What is Vibe Coding"
          >
            <div>
              <h2 className='text-4xl md:text-5xl mb-6'>What is Vibe Coding?</h2>
              <div className='w-20 h-1 bg-brand-red mb-6'></div>
              <div className='space-y-6'>
                <p className='text-xl text-slate-600 leading-relaxed'>
                  Vibe coding is a way of building software with AI where developers describe what they want in plain language, 
                  and large language models (LLMs) handle most of the code generation. This approach changes not only how code 
                  is written, but who can build software and how fast ideas turn into working products.
                </p>
                <p className='text-xl text-slate-600 leading-relaxed'>
                  While vibe coding excels at rapid prototyping, production applications require structure, testing, 
                  security, and maintainability. That's where our cleanup specialists come in.
                </p>
              </div>
            </div>
          </ContentImageSection>
        </div>
      </div>

      {/* Services Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <CardColumn 
            cards={cleanupServices} 
            cols={2}
            title="Our Cleanup Services"
            description="We transform AI-generated code into production-ready applications that scale, perform, and maintain easily."
          />
        </div>
      </div>

      {/* Process Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <HorizontalScroll 
            items={processSteps}
            title="Our Cleanup Process"
            description="A systematic approach to transforming prototypes into production-ready applications"
          />
        </div>
      </div>

      <ServiceCTA
        title="Ready to Take Your Prototype to Production?"
        description="Let our vibe coding cleanup specialists transform your AI-generated code into a robust, scalable application."
        buttonText="Start Your Cleanup Project"
      />

      <Footer />
    </Layout>
  )
}

export default VibeCodingPage
